// native-push.js — push registration + handling inside the Capacitor apps
// (spec: build/02-mobile-app/backend-contracts.md §2-4).
//
// The web PWA path (messaging.js) uses the Firebase JS SDK + service worker;
// NONE of that exists inside the native WebView. Here @capacitor/push-notifications
// talks to FCM/APNs directly, and the backend's android.notification / apns.alert
// blocks (fcm.js) render the system notification — the app only needs to
// register the token and handle taps/foreground events.

import { Capacitor } from '@capacitor/core';
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase.js';

let currentToken = null;
let listenersBound = false;

export function isNative() {
  return Capacitor.isNativePlatform();
}

/**
 * Register for native push: permission → channels → token → updateFcmToken.
 * Returns the token or null (permission denied / not native).
 */
export async function registerNativePush() {
  if (!isNative()) return null;
  const { PushNotifications } = await import('@capacitor/push-notifications');

  let perm = await PushNotifications.checkPermissions();
  if (perm.receive !== 'granted') {
    perm = await PushNotifications.requestPermissions();
  }
  if (perm.receive !== 'granted') return null;

  // Android notification channels — must exist BEFORE the first notification.
  // 'giraops_alerts' (IMPORTANCE_HIGH) = ringing/escalation/coverage alarms;
  // 'giraops_chat' (IMPORTANCE_DEFAULT) = conversation pings. The backend picks
  // the channel per push type (fcm.js channelFor).
  if (Capacitor.getPlatform() === 'android') {
    await PushNotifications.createChannel({
      id: 'giraops_alerts',
      name: 'Job alerts',
      description: 'Incoming tickets, escalations, and coverage alarms',
      importance: 5,          // IMPORTANCE_HIGH — heads-up + sound
      visibility: 1,          // VISIBILITY_PUBLIC — show on lock screen
      vibration: true,
      lights: true,
    });
    await PushNotifications.createChannel({
      id: 'giraops_chat',
      name: 'Job chat',
      description: 'Messages on your active jobs',
      importance: 3,          // IMPORTANCE_DEFAULT
      visibility: 1,
      vibration: true,
    });
  }

  const token = await new Promise((resolve) => {
    let settled = false;
    PushNotifications.addListener('registration', (t) => {
      if (!settled) { settled = true; resolve(t.value); }
    });
    PushNotifications.addListener('registrationError', (err) => {
      console.error('[native-push] registration error', err);
      if (!settled) { settled = true; resolve(null); }
    });
    PushNotifications.register();
    // Safety valve — some devices never fire either event.
    setTimeout(() => { if (!settled) { settled = true; resolve(null); } }, 15000);
  });
  if (!token) return null;

  currentToken = token;
  await httpsCallable(functions, 'updateFcmToken')({
    token,
    platform: Capacitor.getPlatform(), // 'android' | 'ios'
  });
  bindTapNavigation(PushNotifications);
  return token;
}

/** Route notification taps like the web SW's notificationclick handler. */
function bindTapNavigation(PushNotifications) {
  if (listenersBound) return;
  listenersBound = true;
  PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
    const data = action?.notification?.data || {};
    let url;
    if (data.type === 'chat-message') url = '/';           // RoleHome routes by role
    else if (String(data.type || '').startsWith('cover-')) url = '/responder/cover-requests';
    else url = '/responder';
    window.location.assign(url);
  });
}

/** Foreground events → same shape the web foreground handler receives. */
export async function listenNativeForeground(handler) {
  if (!isNative()) return () => {};
  const { PushNotifications } = await import('@capacitor/push-notifications');
  const sub = await PushNotifications.addListener('pushNotificationReceived', (n) => {
    handler(n?.data || {});
  });
  return () => sub.remove();
}

export async function unregisterNativePush() {
  if (!isNative() || !currentToken) return;
  try {
    await httpsCallable(functions, 'removeFcmToken')({ token: currentToken });
  } catch (err) {
    console.warn('[native-push] removeFcmToken failed', err);
  }
  currentToken = null;
}
